import { useParams, Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { api, storageUrl } from "../api";
import { formatPrice } from "../data/currency";
import ReviewForm from "../components/ReviewForm";
import { useAuth } from "../context/AuthContext";
import { GameDetailSkeleton } from "../components/Skeleton";

function GameDetail({ onAction }) {
  const { user } = useAuth();
  const { id } = useParams();
  const [game, setGame] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);
  const [isOwned, setIsOwned] = useState(false);
  const [isWished, setIsWished] = useState(false);

  const [showPayment, setShowPayment] = useState(false);
  const [phone, setPhone] = useState(user?.phone || "");
  const [paymentStep, setPaymentStep] = useState("input");
  const [paymentError, setPaymentError] = useState("");
  const [pollCount, setPollCount] = useState(0);
  const [isPaying, setIsPaying] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get(`/games/${id}`).then((data) => {
      setGame(data);
      setReviews(data.reviews || []);
      setLoading(false);
    });

    if (user) {
      Promise.all([api.get("/library"), api.get("/wishlist")]).then(([lib, wl]) => {
        const gameId = Number(id);
        setIsOwned(lib.some((g) => g.id === gameId));
        setIsWished(wl.some((g) => g.id === gameId));
      });
    }
  }, [id, user]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  if (loading) return <GameDetailSkeleton />;
  if (!game) return <div className="not-found">Game not found</div>;

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : "No ratings yet";

  const handleReview = async (reviewData) => {
    const newReview = await api.post(`/games/${game.id}/reviews`, reviewData);
    setReviews((prev) => [...prev, newReview]);
  };

  const handleBuy = () => {
    setPhone(user?.phone || "");
    setPaymentStep("input");
    setPaymentError("");
    setShowPayment(true);
  };

  const handleInitiatePayment = async (e) => {
    e.preventDefault();
    setPaymentError("");
    setIsPaying(true);

    if (!phone || phone.length < 9) {
      setPaymentError("Enter a valid phone number (at least 9 digits)");
      setIsPaying(false);
      return;
    }

    try {
      const result = await api.post("/payments/create-order", {
        game_id: game.id,
        buyer_phone: phone,
      });

      if (result.status === "success") {
        setPaymentStep("polling");
        setPollCount(0);
        startPolling(result.data.transaction_id);
      } else {
        setPaymentError(result.message || "Payment initiation failed");
      }
    } catch (err) {
      setPaymentError(err.message || "Payment initiation failed");
    } finally {
      setIsPaying(false);
    }
  };

  const startPolling = (txId) => {
    let attempts = 0;
    const maxAttempts = 30;

    pollRef.current = setInterval(async () => {
      attempts++;
      setPollCount(attempts);

      try {
        const result = await api.post("/payments/check-status", {
          transaction_id: txId,
        });

        if (result.data.payment_status === "COMPLETED") {
          clearInterval(pollRef.current);
          await api.post(`/payments/complete/${txId}`);
          setIsOwned(true);
          setShowPayment(false);
          if (onAction) onAction();
        } else if (result.data.payment_status === "FAILED" || result.data.payment_status === "CANCELLED") {
          clearInterval(pollRef.current);
          setPaymentStep("failed");
          setPaymentError("Payment was " + result.data.payment_status.toLowerCase() + ". Please try again.");
        }
      } catch (err) {
        // keep polling
      }

      if (attempts >= maxAttempts) {
        clearInterval(pollRef.current);
        setPaymentStep("timeout");
        setPaymentError("Payment is taking too long. You can check your phone for the payment prompt.");
      }
    }, 4000);
  };

  const handleToggleWishlist = async () => {
    if (isWished) {
      await api.delete(`/games/${game.id}/wishlist`);
      setIsWished(false);
    } else {
      await api.post(`/games/${game.id}/wishlist`);
      setIsWished(true);
    }
    if (onAction) onAction();
  };

  const closePayment = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setShowPayment(false);
  };

  const latestVersion = game.versions && game.versions.length > 0 ? game.versions[0] : null;

  return (
    <div className="game-detail">
      <div className="game-detail-hero">
        <img src={storageUrl(game.cover_path)} alt={game.title} />
        <div className="game-detail-info">
          <h1>{game.title}</h1>
          <span className="game-detail-genre">{game.category ? game.category.name : "Unknown"}</span>
          {game.developer && <p className="game-detail-developer">by {game.developer.username}</p>}
          <p className="game-detail-desc">{game.description}</p>
          <div className="game-detail-meta">
            <span className="game-detail-rating">★ {avgRating}</span>
            <span className="game-detail-price">{formatPrice(game.price)}</span>
          </div>
          <div className="game-detail-actions">
            {!user ? (
              <Link to="/login" className="buy-btn" style={{ textAlign: "center" }}>
                Login to Purchase
              </Link>
            ) : isOwned ? (
              latestVersion?.download_path ? (
                <a
                  href={latestVersion.download_path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-btn"
                >
                  Download APK
                </a>
              ) : (
                <span className="buy-btn" style={{ opacity: 0.6, cursor: "default" }}>No download available</span>
              )
            ) : (
              <button className="buy-btn" onClick={handleBuy}>
                Buy - {formatPrice(game.price)}
              </button>
            )}
            {user && (
              <button
                className={`wishlist-btn ${isWished ? "wishlisted" : ""}`}
                onClick={handleToggleWishlist}
              >
                {isWished ? "♥ Wishlisted" : "♡ Add to Wishlist"}
              </button>
            )}
          </div>
        </div>
      </div>

      {showPayment && (
        <div className="admin-overlay" onClick={closePayment}>
          <div className="admin-modal payment-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Pay with Mobile Money</h3>

            {paymentStep === "input" && (
              <>
                <p style={{ color: "var(--text-muted)", marginBottom: 16, fontSize: "0.9rem" }}>
                  Enter your phone number to pay <strong style={{ color: "var(--gold)" }}>{formatPrice(game.price)}</strong> for <strong style={{ color: "var(--white)" }}>{game.title}</strong>
                </p>
                {paymentError && <p className="auth-error">{paymentError}</p>}
                <form onSubmit={handleInitiatePayment} className="auth-form">
                  <label>
                    Phone Number (M-Pesa, Airtel Money, Tigo Pesa, HaloPesa)
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      placeholder="e.g. 0712345678"
                    />
                  </label>
                  <div className="admin-form-actions">
                    <button type="submit" className="auth-submit" disabled={isPaying}>
                      {isPaying ? (
                        <span className="btn-loading">
                          <span className="spinner-dot" />
                          Processing...
                        </span>
                      ) : (
                        "Pay Now"
                      )}
                    </button>
                    <button type="button" className="auth-submit" style={{ background: "var(--navy-border)" }} onClick={closePayment}>Cancel</button>
                  </div>
                </form>
              </>
            )}

            {paymentStep === "polling" && (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div className="payment-status-visual">
                  <div className="payment-pulse" />
                  <p className="payment-status-title">Processing your payment...</p>
                  <p className="payment-status-subtitle">
                    A payment prompt has been sent to your phone ({phone}).
                    <br />
                    Please enter your mobile money PIN to authorize the payment.
                  </p>
                </div>
                <div className="payment-progress">
                  <div className="payment-progress-bar" style={{ width: `${Math.min((pollCount / 30) * 100, 100)}%` }} />
                </div>
                <p className="payment-poll-text">
                  {pollCount < 30
                    ? `Waiting for confirmation... ${pollCount}/30`
                    : "Still checking..."}
                </p>
                <button
                  className="auth-submit"
                  style={{ background: "var(--navy-border)", marginTop: 16 }}
                  onClick={closePayment}
                >
                  Cancel
                </button>
              </div>
            )}

            {(paymentStep === "failed" || paymentStep === "timeout") && (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <p className="auth-error" style={{ marginBottom: 16 }}>{paymentError}</p>
                <div className="admin-form-actions" style={{ justifyContent: "center" }}>
                  <button className="auth-submit" onClick={() => setPaymentStep("input")}>Try Again</button>
                  <button className="auth-submit" style={{ background: "var(--navy-border)" }} onClick={closePayment}>Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {game.screenshots && game.screenshots.length > 0 && (
        <div className="game-screenshots">
          <h2>Screenshots</h2>
          <div className="screenshots-grid">
            {game.screenshots.map((s) => (
              <img
                key={s.id}
                src={storageUrl(s.path)}
                alt="Screenshot"
                className="screenshot-thumb"
                onClick={() => setSelectedScreenshot(storageUrl(s.path))}
              />
            ))}
          </div>
          {selectedScreenshot && (
            <div className="screenshot-overlay" onClick={() => setSelectedScreenshot(null)}>
              <img src={selectedScreenshot} alt="Screenshot full" />
            </div>
          )}
        </div>
      )}

      {(game.trailer_url || game.trailer_path) && (
        <div className="game-trailer">
          <h2>Trailer</h2>
          <div className="trailer-wrapper">
            {game.trailer_url && game.trailer_url.includes("youtube.com/embed") ? (
              <iframe src={game.trailer_url} title="Game Trailer" allowFullScreen />
            ) : (
              <video
                src={game.trailer_path ? storageUrl(game.trailer_path) : game.trailer_url}
                controls
                className="trailer-video"
              />
            )}
          </div>
        </div>
      )}

      {game.versions && game.versions.length > 0 && (
        <div className="game-versions">
          <h2>Versions</h2>
          <div className="versions-list">
            {game.versions.map((v) => (
              <div key={v.id} className="version-card">
                <div className="version-info">
                  <strong>v{v.version}</strong>
                  <span className="version-date">{new Date(v.created_at).toLocaleDateString()}</span>
                  <p>{v.changelog}</p>
                </div>
                {isOwned && v.download_path && (
                  <a
                    href={v.download_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="download-btn-sm"
                  >
                    Download
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="game-detail-reviews">
        <h2>Reviews ({reviews.length})</h2>
        {reviews.length === 0 ? (
          <p className="no-reviews">No reviews yet. Be the first!</p>
        ) : (
          reviews.map((r) => (
            <div key={r.id} className="review-card">
              <div className="review-header">
                <strong>{r.user?.username || "Anonymous"}</strong>
                <span>★ {r.rating}</span>
              </div>
              <p>{r.text}</p>
            </div>
          ))
        )}
        {user && <ReviewForm onSubmit={handleReview} />}
      </div>
    </div>
  );
}

export default GameDetail;
